// shareCards.ts — the pure parts of "Make your card" on the share page
// (job mct_7e138a6a-e47; design docs/plans/event-share-card-generator.md in
// tsb-programs-command-centre). ShareCardMaker.tsx draws; these decide.
//
// ★ The card is the CC background plus at most a photo circle and two lines
//   of text, drawn at each size's exact pixels from the pack's slots. Nothing
//   here uploads anything: the photo stays on the person's device.

/** A text slot as the worker hands it over (share-cards-core.ts TextSlot). */
export interface CardTextSlot {
  x: number; y: number; w: number;
  align: 'left' | 'center' | 'right';
  maxPx: number; minPx: number; weight: number; colour: string;
}

/** The largest whole font size from maxPx down to minPx at which `text`
 *  fits `w`, or null when even minPx is too wide (the page then asks for
 *  something shorter rather than cropping letters). Empty text fits. */
export function fitFontPx(text: string, slot: Pick<CardTextSlot, 'w' | 'maxPx' | 'minPx'>, widthAt: (px: number) => number): number | null {
  if (!text.trim()) return slot.maxPx;
  for (let px = Math.floor(slot.maxPx); px >= Math.ceil(slot.minPx); px -= 1) {
    if (widthAt(px) <= slot.w) return px;
  }
  return null;
}

/** How the photo sits in its circle, set once and reused on every size:
 *  zoom ≥ 1 over cover-fit, and the image centre's offset from the circle
 *  centre in circle diameters. */
export interface PhotoCrop { zoom: number; x: number; y: number }
export const DEFAULT_CROP: PhotoCrop = { zoom: 1, x: 0, y: 0 };
export const MAX_ZOOM = 4;

/** Keep the photo covering the whole circle: zoom within 1..MAX_ZOOM and the
 *  offset no further than the image's overhang on each axis. */
export function clampCrop(c: PhotoCrop, imgW: number, imgH: number): PhotoCrop {
  const zoom = Math.min(MAX_ZOOM, Math.max(1, Number.isFinite(c.zoom) ? c.zoom : 1));
  const base = 1 / Math.min(imgW, imgH); // cover-fit, per circle diameter
  const halfX = Math.max(0, (imgW * base * zoom - 1) / 2);
  const halfY = Math.max(0, (imgH * base * zoom - 1) / 2);
  const clip = (v: number, h: number) => Math.min(h, Math.max(-h, Number.isFinite(v) ? v : 0));
  return { zoom, x: clip(c.x, halfX), y: clip(c.y, halfY) };
}

/** Where to draw the image so the crop fills a circle at (cx, cy) radius r. */
export function photoRect(c: PhotoCrop, imgW: number, imgH: number, cx: number, cy: number, r: number): { x: number; y: number; w: number; h: number } {
  const k = clampCrop(c, imgW, imgH);
  const d = 2 * r;
  const scale = (d / Math.min(imgW, imgH)) * k.zoom;
  const w = imgW * scale;
  const h = imgH * scale;
  return { x: cx + k.x * d - w / 2, y: cy + k.y * d - h / 2, w, h };
}

/** A download name: na-summit-2026-v1-speaking-square.png. */
export function cardFileName(version: string, role: string, size: string): string {
  const safe = (s: string) => s.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  return `${safe(version) || 'card'}-${safe(role)}-${safe(size)}.png`;
}

// ─── Download all: one zip, built in the browser ───────────────────────────

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** A zip of the given files, stored (PNGs are already compressed), with
 *  plain ASCII names. Readable by every phone and desktop unzipper. */
export function zipStore(files: Array<{ name: string; bytes: Uint8Array }>): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  for (const f of files) {
    const name = enc.encode(f.name);
    const crc = crc32(f.bytes);
    const size = f.bytes.length;
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, 0, true);
    lv.setUint16(8, 0, true); // stored
    lv.setUint16(10, 0, true);
    lv.setUint16(12, 0x21, true); // 1 Jan 1980
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);
    lv.setUint32(22, size, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);
    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, 0, true);
    cv.setUint16(14, 0x21, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    central.set(name, 46);
    locals.push(local, f.bytes);
    centrals.push(central);
    offset += local.length + size;
  }
  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  const out = new Uint8Array(offset + centralSize + end.length);
  let at = 0;
  for (const part of [...locals, ...centrals, end]) { out.set(part, at); at += part.length; }
  return out;
}
